<template>
  <AppEventContainer>
    <!-- Success Message -->
    <SuccessConfirmation v-if="userTransaction?.status === 'complete'">
      Your event registration is confirmed, and a receipt has been emailed to you.
    </SuccessConfirmation>

    <!-- Event Header -->
    <EventListItem :poker-run-event="pokerRun" :show-details="false" class="mb-8" />

    <!-- Transaction Details -->
    <div class="flex gap-2 justify-between">
      <EventSummaryItem label="Transaction time">
        <p v-if="userTransaction?.transactionTimestamp">
          {{ formatFullDate(userTransaction.transactionTimestamp.toDate()) }}
        </p>
        <p v-if="userTransaction?.transactionTimestamp">
          {{ formatTime(userTransaction.transactionTimestamp.toDate()) }}
        </p>
      </EventSummaryItem>

      <EventSummaryItem v-if="userTransaction?.transactionDetails.last4" label="Payment method">
        Card ending in {{ userTransaction?.transactionDetails.last4 }}
      </EventSummaryItem>
    </div>

    <!-- Confirmation Code -->
    <div class="mb-8">
      <EventSummaryItem label="Confirmation code">
        <div class="">
          <p class="font-medium">{{ userTransaction?.transactionDetails.transactionId }}</p>
          <button @click="handleCopy(userTransaction?.transactionDetails.transactionId)"
            class="text-action hover:text-action-hover flex items-center">
            Copy code
            <i class="text-action fa-copy far ml-1"></i>
          </button>
        </div>
      </EventSummaryItem>
    </div>

    <!-- Order Summary -->
    <div class="mb-8">
      <EventCost :line-items="lineItemsWithServiceFee" :total-amount="userTransaction?.transactionDetails.amount ?? 0"
        :show-change-button="false" />
    </div>

    <!-- Action Button -->
    <AppButton title="Go to your events" :disabled="isBusy" @click="openEvent" class="w-full" />
  </AppEventContainer>
</template>
<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import EventListItem from "@/components/EventListItem.vue";
import EventCost from "@/components/EventCost.vue";
import AppButton from "@/components/AppButton.vue";
import EventSummaryItem from "@/components/EventSummaryItem.vue";
import SuccessConfirmation from "@/components/SuccessConfirmation.vue";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { UserTransaction } from "@models/UserTransaction";
import { useUserProfileStore } from "@/store/UserProfile";
import useDate from "@/modules/useDate";
import { Item } from "@models/Item";
import { Unsubscribe } from "firebase/firestore";
import useCommunication from "@/modules/useCommunication";
import useLocalization from "@/modules/useLocalization";
import Constants from "@/models/Constants";

const { formatTime, formatFullDate } = useDate();
const route = useRoute();
const router = useRouter();
const { profile } = useUserProfileStore();
const { copyToClipboard } = useCommunication();
const { formatCurrency } = useLocalization();
const isBusy = ref<boolean>(false);
const pokerRun = ref<PokerRunEvent>();
const transactionTypeDisplay = ref<string>();
const userTransaction = ref<UserTransaction | undefined>();
const lineItemsWithServiceFee = computed<Item[]>(() => {
  if (userTransaction.value === undefined) {
    return [];
  }

  const fees = userTransaction.value.transactionDetails.lineItems;

  const sumOfFees = fees.reduce((prev, curr) => prev + curr.amount, 0);

  if (userTransaction.value.transactionDetails.amount - sumOfFees > 0) {
    return [
      ...userTransaction.value.transactionDetails.lineItems,
      {
        name: Constants.SERVICE_FEE,
        itemType: Constants.SERVICE_FEE,
        amount: userTransaction.value.transactionDetails.amount - sumOfFees,
      },
    ];
  }
  return [...userTransaction.value.transactionDetails.lineItems];
});

const messageType = computed(() => {
  if (userTransaction.value === undefined) {
    return "info";
  }

  if (userTransaction.value?.status === "complete") {
    return "success";
  }

  if (userTransaction.value?.status === "failed") {
    return "danger";
  }

  return "info";
});

const openEvent = () => {
  isBusy.value = true;
  setTimeout(() => {
    router.push({ name: "RegisteredEvent", params: { id: pokerRun.value?.id as string } });
  }, 2000);
};

let unsubscribe: Unsubscribe | null = null;

onBeforeUnmount(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});

const initialize = async () => {
  pokerRun.value = await PokerRunEventDataService.get(route.params.id as string);
  unsubscribe = await UserTransactionDataService.getUserTransactionSubscription(
    profile.id,
    route.params.transactionId as string,
    (transaction) => {
      userTransaction.value = transaction;
    }
  );
  transactionTypeDisplay.value =
    userTransaction.value?.transactionType === "registration" ? "registration" : "purchase";
};

initialize();

const handleCopy = async (text?: string) => {
  if (text) {
    await copyToClipboard(text);
  }
};
</script>
